import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  Form,
  Modal,
  ProgressBar,
  Row,
  Spinner,
} from "react-bootstrap";
import { Chat, Send } from "react-bootstrap-icons";
import { useForm, Controller } from "react-hook-form";
import FalconCloseButton from "components/common/FalconCloseButton";
import { useDispatch, useSelector } from "react-redux";
import {
  GetAllClaimPreAuthAllList,
  GetPreauthorizationRequest,
  SendCommunicationMessage,
  ViewPreauthorization,
} from "../config/preauthorizationApi";
import { getPathFromUrl } from "helpers/utils";
import { toast } from "react-toastify";
import {
  compressPDF,
  toBase64,
  compressImage,
  formatFileSize,
} from "../../utils";
import TextInput from "components/common/custom/forms/BootTextField/TextField";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faEye } from "@fortawesome/free-solid-svg-icons";
import IconButton from "components/common/IconButton";
import { Trash } from "react-bootstrap-icons";
import Flex from "components/common/Flex";

const SendCommunicationRequestModal = ({
  modal,
  setModal,
  modalRow,
  parentCommunicationId = null,
}) => {
  const dispatch = useDispatch();
  const { communcationDetails, preauthorizationRequest, viewPreauthorizationInfo } = useSelector(
    (state) => state.preauthorization
  );

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      communication_message: "",
      communication_category: "",
      communication_reason: "",
      request_priority: "",
      item: "",
      attachment: null,
    },
  });

  const [file, setFile] = useState(null);
  const [compressionProgress, setCompressionProgress] = useState(0);
  const [compressionError, setCompressionError] = useState(null);



  const { categories, priorities, reason } = useMemo(() => {
    const cats = [],
      prios = [],
      reason = [];
    communcationDetails?.forEach((item) => {
      if (item.cmnreqdtl_type === "category") {
        cats.push(item);
      } else if (item.cmnreqdtl_type === "priority") {
        prios.push(item);
      } else if (item.cmnreqdtl_type === "reasoncode") {
        reason.push(item);
      }
    });
    return { categories: cats, priorities: prios, reason };
  }, [communcationDetails]);

  useEffect(() => {
    if (modal && communcationDetails?.length === 0) {
      dispatch(GetAllClaimPreAuthAllList());
    }
  }, [modal, communcationDetails, dispatch]);

  useEffect(() => {
    const fetchPreauthData = async () => {
      if (!modalRow?.preauthfull_id) return;
      try {
        const viewResponse = await dispatch(
          ViewPreauthorization({
            preauthid: modalRow?.preauthfull_id,
            claim_type: modalRow?.claim_type,
          })
        ).unwrap();

        if (viewResponse?.success) {

          // Load first existing attachment from payload_json.sup_info_attch
          const payloadClaim = viewResponse?.data?.payload_json;
          const attachmentsFromPayload = Object.values(
            payloadClaim?.sup_info_attch || {}
          );
          if (attachmentsFromPayload.length > 0 && !file) {
            const entry = attachmentsFromPayload[0]; // Take only the first attachment
            if (entry?.contentType && entry?.data && entry?.title) {
              const mime = entry.contentType || "application/pdf";
              let blob;
              try {
                blob = new Blob(
                  [Uint8Array.from(atob(entry.data), (c) => c.charCodeAt(0))],
                  { type: mime }
                );
              } catch (error) {
                console.error(
                  `Error decoding base64 for file ${entry.title}:`,
                  error
                );
                return;
              }

              const fileObj = new File([blob], entry.title, {
                type: mime,
                lastModified: new Date(entry.creation || Date.now()).getTime(),
              });

              const fileData = {
                originalFile: fileObj,
                compressedFile: fileObj,
                preview: mime.includes("pdf")
                  ? null
                  : URL.createObjectURL(fileObj),
                type: mime,
                path: entry.title,
                base64: entry.data,
                title: entry.title,
                creation: entry.creation || new Date().toISOString(),
              };

              setFile(fileData);
              setValue("attachment", {
                contentType: fileData.type,
                title: fileData.title,
                originalName: fileData.title,
                size: fileObj.size,
                creation: fileData.creation,
                data: fileData.base64,
              });
            }
          }
        }
      } catch (error) {
        console.error("Failed to fetch preauthorization data:", error);
      }
    };

    if (modal) {
      fetchPreauthData();
    }

    // Cleanup preview
    return () => {
      if (file?.preview) {
        URL.revokeObjectURL(file.preview);
      }
    };
  }, [modal, modalRow]);

  const calculateFileSize = (file) => {
    if (!file || !file.data) return 0;
    const base64Data = file.data.split(",")[1] || file.data;
    try {
      const binaryString = atob(base64Data);
      return binaryString.length;
    } catch (error) {
      console.error(`Error decoding base64 for file ${file?.title}:`, error);
      return 0;
    }
  };

  const handleFileChange = async (file, onChange) => {
    if (!file) {
      setFile(null);
      onChange(null);
      setCompressionProgress(0);
      setCompressionError(null);
      clearErrors("attachment");
      return;
    }

    const allowedTypes = ["application/pdf", "image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      setCompressionError("Only PDF, JPEG, or PNG files are allowed");
      setError("attachment", {
        type: "manual",
        message: "Only PDF, JPEG, or PNG files are allowed",
      });
      toast.warning("Only PDF or image files are allowed");
      return;
    }

    setCompressionProgress(10);
    setCompressionError(null);

    try {
      let compressedFile = file;

      if (file.type === "application/pdf") {
        setCompressionProgress(40);
        compressedFile = await compressPDF(file);
        setCompressionProgress(80);
      } else if (file.type.startsWith("image/")) {
        compressedFile = await compressImage(file);
      }

      const maxSize = 10 * 1024 * 1024; // 10MB
      if (compressedFile.size > maxSize) {
        setCompressionError(
          `Compressed file exceeds 10MB limit (${formatFileSize(
            compressedFile.size
          )})`
        );
        setError("attachment", {
          type: "manual",
          message: `Compressed file exceeds 10MB limit (${formatFileSize(
            compressedFile.size
          )})`,
        });
        toast.warning(`Compressed file exceeds 10MB limit`);
        setCompressionProgress(0);
        return;
      }

      const base64Data = await toBase64(compressedFile);
      console.log({ base64Data });

      const sanitizedName = file.name
        .replace(/\s+/g, "_")
        .replace(/\([^)]*\)/g, "")
        .replace(/[^\w.-]/g, "");
      const ext = sanitizedName
        .substring(sanitizedName.lastIndexOf("."))
        .toLowerCase();
      const hash = `${Date.now()}`;
      const title = `${hash}${ext}`;

      const newFile = {
        originalFile: file,
        compressedFile,
        preview: compressedFile.type.includes("pdf")
          ? null
          : URL.createObjectURL(compressedFile),
        type: compressedFile.type,
        path: file.name,
        data: base64Data,
        title,
        creation: new Date().toISOString(),
      };
      // Check file size
      const fileSize = calculateFileSize(newFile);
      if (fileSize > maxSize) {
        setCompressionError(
          `File size exceeds 10MB limit (Current: ${formatFileSize(fileSize)})`
        );
        setError("attachment", {
          type: "manual",
          message: `File size exceeds 10MB limit (Current: ${formatFileSize(
            fileSize
          )})`,
        });
        toast.warning(`File size exceeds 10MB limit`);
        setCompressionProgress(0);
        return;
      }

      setFile(newFile);
      console.log({ file });
      onChange({
        contentType: newFile.type,
        title: newFile.title,
        originalName: file.name,
        size: compressedFile.size,
        creation: newFile.creation,
        data: newFile?.data,
      });
      setCompressionProgress(100);
      clearErrors("attachment");
    } catch (error) {
      console.error("Compression error:", error);
      setCompressionError("Error processing file");
      setError("attachment", {
        type: "manual",
        message: "Error processing file",
      });
      toast.error("Error processing file");
      setCompressionProgress(0);
    }
  };

  const onSubmit = async (data) => {
    const payload = {
      preauthid: modalRow?.preauthfull_id,
      comun_itmseq: parseInt(data.item),
      comun_categ: data.communication_category,
      comun_cde: data.communication_reason,
      comun_prior: data.request_priority,
      comun_recvreqid: parentCommunicationId,
      comun_descp: data.communication_message,
      claim_type: modalRow?.claim_type,
    };

    if (data.attachment) {
      payload.attch = {
        contentType: data.attachment.contentType,
        title: data.attachment.title,
        creation: data.attachment.creation,
        data: data.attachment.data,
      };
    }

    try {
      const response = await dispatch(
        SendCommunicationMessage(payload)
      ).unwrap();
      if (response?.success) {
        toast.success(response?.message_en);
        reset();
        setFile(null);
        setModal(false);
      } else {
        toast.error(response?.message_en || "Something went wrong");
      }
    } catch (error) {
      toast.error("Failed to send communication request");
    }
  };

  return (
    <Modal
      show={modal}
      centered
      onHide={() => {
        setModal(false);
        reset();
        setFile(null);
      }}
      dialogClassName="modal-md"
    >
      <Modal.Header className="d-flex bg-primary text-white justify-content-between align-items-center ps-4 pe-4 pt-3">
        <Modal.Title as="h5" className=" fs-11">
          <Chat className="me-1" /> Send Communication Request
        </Modal.Title>
        <FalconCloseButton
          size="sm"
          onClick={() => {
            setModal(false);
            reset();
            setFile(null);
          }}
          className="position-relative"
        />
      </Modal.Header>

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Modal.Body>
          <Row>
            <Col xs={12} sm={6} xl={4}>
              <Controller
                name="communication_category"
                control={control}
                rules={{ required: "Category is required" }}
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label className="fs-11">
                      Communication Category
                    </Form.Label>
                    <Form.Select
                      {...field}
                      size="sm"
                      isInvalid={!!errors.communication_category}
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option
                          key={cat.cmnreqdtl_code}
                          value={cat.cmnreqdtl_code}
                        >
                          {cat.cmnreqdtl_name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.communication_category?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
              />
            </Col>

            <Col xs={12} sm={6} xl={4}>
              <Controller
                name="communication_reason"
                control={control}
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label className="fs-11">
                      Communication Reason
                    </Form.Label>
                    <Form.Select {...field} size="sm">
                      <option value="">Select Reason</option>
                      {reason.map((cat) => (
                        <option
                          key={cat.cmnreqdtl_code}
                          value={cat.cmnreqdtl_code}
                        >
                          {cat.cmnreqdtl_name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                )}
              />
            </Col>

            <Col xs={12} sm={6} xl={4}>
              <Controller
                name="request_priority"
                control={control}
                rules={{ required: "Priority is required" }}
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label className="fs-11">Request Priority</Form.Label>
                    <Form.Select
                      {...field}
                      size="sm"
                      isInvalid={!!errors.request_priority}
                    >
                      <option value="">Select Priority</option>
                      {priorities.map((pri) => (
                        <option
                          key={pri.cmnreqdtl_code}
                          value={pri.cmnreqdtl_code}
                        >
                          {pri.cmnreqdtl_name}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.request_priority?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
              />
            </Col>

            <Col xs={12} sm={12} md={6}>
              <Controller
                name="item"
                control={control}
                rules={{ required: "Item is required" }}
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label className="fs-11">Item</Form.Label>
                    <Form.Select {...field} size="sm" isInvalid={!!errors.item}>
                      <option value="">Select Item</option>
                      {viewPreauthorizationInfo?.provider_request?.service_procedures?.map((item, index) => {
                        const code = item?.itemcode || "";
                        const display = item?.itemdescp || "";
                        return (
                          <option key={index} value={index + 1}>
                            {`${code} - ${display}`}
                          </option>
                        );
                      })}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">
                      {errors.item?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
              />
            </Col>

            <Col xs={12} sm={12} md={6}>
              <Controller
                name="attachment"
                control={control}
                rules={{ required: false }}
                render={({ field }) => (
                  <div>
                    <TextInput
                      name={field.name}
                      label="Attachment"
                      type="file"
                      control={control}
                      required={false}
                      rules={{ required: false }}
                      errors={errors}
                      onChange={(file) =>
                        handleFileChange(file, field.onChange)
                      }
                      value={
                        file?.originalFile?.name ||
                        field.value?.originalName ||
                        "Choose file..."
                      }
                    />
                    {compressionProgress > 0 && compressionProgress < 100 && (
                      <div className="text-center mt-2">
                        <Spinner
                          animation="border"
                          variant="primary"
                          size="sm"
                        />
                        <ProgressBar
                          now={compressionProgress}
                          label={`${compressionProgress}%`}
                          className="mt-2"
                        />
                        <p className="mt-2 mb-0">Compressing file...</p>
                      </div>
                    )}
                    {compressionError && (
                      <div className="text-danger mt-2 fs-10">
                        {compressionError}
                      </div>
                    )}
                  </div>
                )}
              />
            </Col>

            <Col xs={12}>
              <Controller
                name="communication_message"
                control={control}
                rules={{ required: "Message is required" }}
                render={({ field }) => (
                  <Form.Group>
                    <Form.Label className="fs-11">
                      Communication Message
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      size="sm"
                      placeholder="Enter your message"
                      {...field}
                      isInvalid={!!errors.communication_message}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.communication_message?.message}
                    </Form.Control.Feedback>
                  </Form.Group>
                )}
              />
            </Col>
          </Row>
        </Modal.Body>

        <Modal.Footer className="border-top pt-2">
          <Button
            size="sm"
            variant="primary"
            type="submit"
            className="fs-11"
            disabled={compressionProgress > 0 && compressionProgress < 100}
          >
            <Send className="me-1" /> Send
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default SendCommunicationRequestModal;
